<script setup>
import { FilterMatchMode } from "primevue/api";
import axios from "axios";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const options = { year: "numeric", month: "numeric", day: "numeric" };

const getDateFormat = (date) => {
  const d = new Date(Date.parse(date));
  return (
    d.toLocaleDateString(undefined, options) +
    " à " +
    d.toLocaleTimeString("fr-FR")
  );
};
</script>

<template>
  <div>
    <div class="card mb-4">
      <div class="flex align-items-center">
        <p class="text-4xl pr-4 pl-2 text-700">{{ $t("servicesIA.title") }}</p>
        <router-link to="/mesServicesIA/nouveau" class="no-underline">
          <Button id="btn_nouvelle_generation" class="border-round-xl h-2rem">
            {{ $t("servicesIA.newGeneration") }}
          </Button>
        </router-link>
      </div>

      <div class="h-full p-2">
        <TabView
          id="barreNavigation_servicesIA"
          :pt="{
            panelContainer: () => ({
              class: 'p-0',
            }),
          }"
        >
          <!-- Onglet En cours -->
          <TabPanel
            :header="$t('servicesIA.inProgress')"
            :pt="{
              header: () => ({
                class: 'border-left-2 border-top-2 border-right-2 border-gray-300',
              }),
            }"
          >
            <div class="border-2 border-gray-300 border-top-none">
              <DataTable
                id="liste_servicesIA_enCours"
                :loading="loading"
                :value="generationsEnCours"
                v-model:filters="filters"
                dataKey="id"
                :paginator="true"
                :rows="10"
                :rowsPerPageOptions="[5, 10, 15]"
              >
                <template #header>
                  <div class="flex flex-wrap gap-2">
                    <span class="p-input-icon-right">
                      <InputText
                        v-model="filters['global'].value"
                        :placeholder="$t('servicesIA.search') + '...'"
                      />
                      <i class="pi pi-search" />
                    </span>
                  </div>
                </template>

                <template #empty>
                  <div class="p-3 text-center text-500">
                    {{ $t("servicesIA.noInProgress") }}
                  </div>
                </template>

                <Column field="formatName" :header="$t('servicesIA.formatName')" sortable></Column>
                <Column field="createdAt" :header="$t('servicesIA.date')" sortable>
                  <template #body="slotProps">
                    {{ getDateFormat(slotProps.data.createdAt) }}
                  </template>
                </Column>
                <Column field="stepLabel" :header="$t('servicesIA.status')">
                  <template #body="slotProps">
                    <Tag
                      :value="slotProps.data.stepLabel"
                      :severity="getStepSeverity(slotProps.data.step)"
                    />
                  </template>
                </Column>
                
                <Column :header="$t('servicesIA.consult')">
                  <template #body="slotProps">
                    <router-link :to="'/mesServicesIA/nouveau?id=' + slotProps.data.id">
                      <i
                        class="pi pi-eye cursor-pointer"
                        style="color: #c4165a; font-size: 1.5rem"
                      />
                    </router-link>
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- Onglet Terminés -->
          <TabPanel
            :header="$t('servicesIA.completed')"
            :pt="{
              header: () => ({
                class: 'border-left-2 border-top-2 border-right-2 border-gray-300',
              }),
            }"
          >
            <div class="border-2 border-gray-300 border-top-none">
              <DataTable
                id="liste_servicesIA_termines"
                :loading="loadingTermines"
                :value="generationsTerminees"
                v-model:filters="filtersTermines"
                dataKey="id"
                :paginator="true"
                :rows="10"
                :rowsPerPageOptions="[5, 10, 15]"
              >
                <template #header>
                  <div class="flex flex-wrap gap-2">
                    <span class="p-input-icon-right">
                      <InputText
                        v-model="filtersTermines['global'].value"
                        :placeholder="$t('servicesIA.search') + '...'"
                      />
                      <i class="pi pi-search" />
                    </span>
                  </div>
                </template>

                <template #empty>
                  <div class="p-3 text-center text-500">
                    {{ $t("servicesIA.noCompleted") }}
                  </div>
                </template>

                <Column field="formatName" :header="$t('servicesIA.formatName')" sortable></Column>
                <Column field="createdAt" :header="$t('servicesIA.date')" sortable>
                  <template #body="slotProps">
                    {{ getDateFormat(slotProps.data.createdAt) }}
                  </template>
                </Column>
                <Column field="stepLabel" :header="$t('servicesIA.status')">
                  <template #body="slotProps">
                    <Tag
                      :value="slotProps.data.stepLabel"
                      :severity="getStepSeverity(slotProps.data.step)"
                    />
                  </template>
                </Column>
                <Column :header="$t('servicesIA.consult')">
                  <template #body="slotProps">
                    <router-link :to="'/mesServicesIA/nouveau?id=' + slotProps.data.id">
                      <i
                        class="pi pi-eye cursor-pointer"
                        style="color: #c4165a; font-size: 1.5rem"
                      />
                    </router-link>
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </div>
  </div>
  <Toast />
  <Button
    class="fixed border-circle shadow-2 bg-blue-500 hover:bg-blue-700 text-white text-xl flex align-items-center justify-content-center"
    style="bottom: 20px; right: 20px; z-index: 9999; width: 3rem; height: 3rem;"
    @click="launchGuide('mes_services_ia')"
  >?</Button>
</template>

<script>
import { startTour } from "@/guide/guide";

export default {
  data() {
    return {
      generationsEnCours: null,
      generationsTerminees: null,
      filters: {},
      filtersTermines: {},
      loading: true,
      loadingTermines: true,
    };
  },
  created() {
    this.initFilters();
  },
  mounted() {
    this.token = localStorage.getItem("token");
    this.fetchListe();
  },
  methods: {
    launchGuide(context) {
      startTour(context, this.$i18n.locale);
    },
    initFilters() {
      const base = { global: { value: null, matchMode: "contains" } };
      this.filters = { ...base };
      this.filtersTermines = { ...base };
    },
    fetchListe() {
      this.loading = true;
      this.loadingTermines = true;

      axios
        .get(process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/liste", {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + this.token,
          },
        })
        .then((response) => {
          this.generationsEnCours = response.data.enCours || [];
          this.generationsTerminees = response.data.termines || [];
          this.loading = false;
          this.loadingTermines = false;
        })
        .catch((e) => {
          if (e?.response?.status === 401) {
            this.$router.push("/login");
          } else {
            this.$toast.add({
              severity: "error",
              summary: this.$t("erreur.erreur"),
              detail: this.$t("erreur.erreurInterne"),
              life: 3000,
            });
            this.loading = false;
            this.loadingTermines = false;
          }
        });
    },
    getStepSeverity(step) {
      if (step === -1) return "danger";
      if (step === 0 || step === -2) return "success";
      return "warning";
    },
  },
};
</script>
